import { AlertTriangle, RefreshCw, ArrowLeft, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState } from 'react';

interface OnboardingErrorCardProps {
  title: string;
  message: string;
  code?: string;
  tip?: string;
  onRetry?: () => void;
  onBack?: () => void;
  showDebugInfo?: boolean;
  debugInfo?: Record<string, any>;
}

export function OnboardingErrorCard({
  title,
  message,
  code,
  tip,
  onRetry,
  onBack,
  showDebugInfo = false,
  debugInfo,
}: OnboardingErrorCardProps) {
  const [isDebugOpen, setIsDebugOpen] = useState(false);

  return (
    <Card className="border-destructive/20 bg-destructive/5">
      <CardHeader>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <CardTitle className="text-lg text-destructive">{title}</CardTitle>
            {code && (
              <Badge variant="outline" className="text-xs border-destructive/20 text-destructive">
                {code}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <p className="text-sm">{message}</p>
        
        {tip && (
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              <strong>Tip:</strong> {tip}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          {onRetry && (
            <Button onClick={onRetry} size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}
          
          {onBack && (
            <Button onClick={onBack} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          )}
        </div>

        {showDebugInfo && debugInfo && (
          <Collapsible open={isDebugOpen} onOpenChange={setIsDebugOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs">
                <Bug className="w-3 h-3 mr-2" />
                {isDebugOpen ? 'Hide' : 'Show'} Debug Info
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="rounded-lg bg-muted p-3">
                <pre className="text-xs overflow-auto max-h-32">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}